import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  deleteRecoveryCodes,
  getRecoveryCodesStatus,
} from "@services/api";
import {
  getCurrentAal,
  listMfaFactors,
  MfaFactor,
  unenrollMfa,
} from "@services/supabaseAPI";
import { useNotification } from "@shared/contexts/NotificationContext";
import MfaEnrollDialog from "./MfaEnrollDialog";

/**
 * "Two-factor authentication" section of the Security panel. Shows
 * enrollment status + remaining recovery codes, and exposes the
 * enroll / regenerate / unenroll actions.
 *
 * Phase 1 caveat: the sign-in MFA challenge isn't wired yet (Phase 2).
 * Regenerate and Unenroll both require an AAL2 session; without the
 * step-up flow, the user only has AAL2 immediately after enrollment.
 * We surface that constraint with a tooltip rather than hiding the
 * buttons, so the affordance is discoverable.
 */
const MfaPanel = () => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  const [remainingCodes, setRemainingCodes] = useState<number | null>(null);
  const [aal, setAal] = useState<"aal1" | "aal2" | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [factorsRes, statusRes, aalRes] = await Promise.all([
      listMfaFactors(),
      getRecoveryCodesStatus(),
      getCurrentAal(),
    ]);
    setFactors(factorsRes);
    setRemainingCodes(
      statusRes.success ? statusRes.data.remaining : null
    );
    setAal(aalRes.current);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enrolled = factors.length > 0;
  const isAal2 = aal === "aal2";

  const handleUnenroll = async () => {
    if (!confirm("Remove two-factor authentication from your account?")) return;
    // Remove every verified factor (usually just one) then wipe the
    // recovery codes server-side. Order matters: unenroll first so a
    // partial failure leaves the user with codes-but-no-factor (still
    // usable for recovery) rather than factor-but-no-codes (locked
    // out on next sign-in).
    for (const f of factors) {
      const res = await unenrollMfa(f.id);
      if (!res.success) {
        showError(res.error || "Could not remove factor");
        return;
      }
    }
    const wipe = await deleteRecoveryCodes();
    if (!wipe.success) {
      // Codes are orphaned but harmless; warn rather than block.
      showError("Factor removed, but recovery codes could not be cleared.");
    } else {
      showSuccess("Two-factor authentication removed.");
    }
    refresh();
  };

  const lowCodes = enrolled && remainingCodes !== null && remainingCodes < 3;

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
      >
        <Box>
          <Typography variant="subtitle1">
            Two-factor authentication
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Checking status…
              </Typography>
            </Box>
          ) : enrolled ? (
            <Typography
              variant="body2"
              color={lowCodes ? "warning.main" : "text.secondary"}
            >
              Enrolled
              {remainingCodes !== null && (
                <> · {remainingCodes} recovery code{remainingCodes === 1 ? "" : "s"} remaining</>
              )}
              {lowCodes && " — regenerate before you run out"}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Add an authenticator app for an extra layer of security at sign-in.
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {!enrolled && !loading && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setEnrollOpen(true)}
            >
              Enable 2FA
            </Button>
          )}
          {enrolled && (
            <>
              <Tooltip
                title={
                  isAal2
                    ? ""
                    : "Sign in with two-factor authentication first."
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={!isAal2}
                    onClick={() => setRegenerateOpen(true)}
                  >
                    Regenerate codes
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  isAal2
                    ? ""
                    : "Sign in with two-factor authentication first."
                }
              >
                <span>
                  <Button
                    color="error"
                    variant="outlined"
                    disabled={!isAal2}
                    onClick={handleUnenroll}
                  >
                    Remove 2FA
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>
      <MfaEnrollDialog
        open={enrollOpen}
        mode="enroll"
        onClose={() => setEnrollOpen(false)}
        onComplete={refresh}
      />
      <MfaEnrollDialog
        open={regenerateOpen}
        mode="regenerate"
        onClose={() => setRegenerateOpen(false)}
        onComplete={refresh}
      />
    </Box>
  );
};

export default MfaPanel;
